import { z } from 'zod'

const overrideValueSchema = z.union([z.number(), z.string()])

export type OverrideValue = z.infer<typeof overrideValueSchema>

/** Why an SME replaced a calculated value, stored next to the override. */
export const overrideJustificationSchema = z.object({
  text: z.string(),
  justifiedBy: z.string(),
  justifiedAt: z.iso.datetime(),
  /** The override value this justification was given for. */
  overrideValue: overrideValueSchema,
})

export type OverrideJustification = z.infer<typeof overrideJustificationSchema>

/** A malformed record reads as missing, so the user is asked for a new reason. */
export const optionalOverrideJustificationSchema = overrideJustificationSchema
  .optional()
  .catch(undefined)

/** A non-blank justification given for exactly this override value. */
export function hasJustificationForOverride(
  justification: OverrideJustification | undefined,
  overrideValue: OverrideValue | undefined
): justification is OverrideJustification {
  return (
    justification !== undefined &&
    justification.overrideValue === overrideValue &&
    justification.text.trim() !== ''
  )
}

/**
 * An override needs a justification while there is a calculated value it
 * replaces, until it has one given for its current value.
 */
export function needsJustification(
  overrideValue: OverrideValue | undefined,
  calculatedValue: OverrideValue | undefined,
  justification: OverrideJustification | undefined
): boolean {
  if (overrideValue === undefined || calculatedValue === undefined) return false
  return !hasJustificationForOverride(justification, overrideValue)
}
